import { useQuery } from '@tanstack/react-query'
import { api, useAuthStore } from '@personic/shared'
import { useImpersonationStore } from '@/stores/impersonationStore'

export default function TenantDashboard() {
  const { user } = useAuthStore()
  const { tenantId: impersonatedTenantId } = useImpersonationStore()
  const tenantId = impersonatedTenantId || user?.tenant_id || ''

  const { data: stats, isLoading } = useQuery({
    queryKey: ['tenant-stats', tenantId],
    queryFn: async () => {
      const [usageRes, profilesRes, keysRes] = await Promise.all([
        api.get('/usage/stats'),
        api.get('/v3/profiles/count'),
        api.get(`/tenants/${tenantId}/api-keys`),
      ])
      return {
        usage: usageRes.data,
        profiles: profilesRes.data,
        apiKeys: keysRes.data,
      }
    },
  })

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading...</div>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      {/* Welcome */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">
          Welcome back{user?.name ? `, ${user.name}` : ''}
        </h1>
        <p className="text-gray-600 mt-1">Here's an overview of your Personic AI usage</p>
      </div>

      {/* Stats Grid */}
      <div className="grid md:grid-cols-4 gap-6">
        <StatCard
          label="Total Profiles"
          value={stats?.profiles?.count || 0}
          description="Active personality profiles"
        />
        <StatCard
          label="API Requests (30d)"
          value={stats?.usage?.total_requests || 0}
          description="Total API calls this month"
        />
        <StatCard
          label="Success Rate"
          value={`${stats?.usage?.success_rate || 0}%`}
          description="Successful API responses"
        />
        <StatCard
          label="Active API Keys"
          value={stats?.apiKeys?.filter((k: { is_active: boolean }) => k.is_active).length || 0}
          description="Keys currently in use"
        />
      </div>

      {/* Usage Chart Placeholder */}
      <div className="card">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">API Usage (Last 30 Days)</h2>
        </div>
        <div className="p-6">
          <div className="h-64 flex items-center justify-center bg-gray-50 rounded-lg">
            <div className="text-center text-gray-500">
              <div className="text-4xl mb-2">📊</div>
              <p>Usage chart will be displayed here</p>
              <p className="text-sm">Powered by Kong Prometheus metrics</p>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions & Recent Activity */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Quick Actions */}
        <div className="card p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
          <div className="space-y-3">
            <a
              href="/dashboard/api-keys"
              className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <div className="flex items-center">
                <span className="text-2xl mr-3">🔑</span>
                <div>
                  <p className="font-medium text-gray-900">Create API Key</p>
                  <p className="text-sm text-gray-500">Generate a new API key for integration</p>
                </div>
              </div>
              <span className="text-gray-400">→</span>
            </a>
            <a
              href="/dashboard/profiles"
              className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <div className="flex items-center">
                <span className="text-2xl mr-3">👤</span>
                <div>
                  <p className="font-medium text-gray-900">Browse Profiles</p>
                  <p className="text-sm text-gray-500">View and manage personality profiles</p>
                </div>
              </div>
              <span className="text-gray-400">→</span>
            </a>
            <a
              href="https://docs.personicai.com"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <div className="flex items-center">
                <span className="text-2xl mr-3">📚</span>
                <div>
                  <p className="font-medium text-gray-900">API Documentation</p>
                  <p className="text-sm text-gray-500">Explore the v3.2 API reference</p>
                </div>
              </div>
              <span className="text-gray-400">↗</span>
            </a>
          </div>
        </div>

        {/* Top Endpoints */}
        <div className="card p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Endpoints</h3>
          <div className="space-y-3">
            {stats?.usage?.endpoints?.slice(0, 5).map(
              (ep: { endpoint: string; count: number; avg_response_time_ms: number }) => (
                <div
                  key={ep.endpoint}
                  className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0"
                >
                  <div>
                    <p className="font-mono text-sm text-gray-900 truncate max-w-xs">
                      {ep.endpoint}
                    </p>
                    <p className="text-xs text-gray-500">{ep.avg_response_time_ms}ms avg</p>
                  </div>
                  <span className="font-semibold text-gray-900">{ep.count}</span>
                </div>
              )
            ) || (
              <div className="text-center py-4 text-gray-500">No endpoint data yet</div>
            )}
          </div>
        </div>
      </div>

      {/* Rate Limit Status */}
      <div className="card p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Rate Limit Status</h3>
        <div className="grid md:grid-cols-3 gap-6">
          <div>
            <p className="text-sm text-gray-500">Plan</p>
            <p className="text-lg font-semibold text-gray-900 capitalize">
              {user?.tenant?.plan || 'Trial'}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Rate Limit</p>
            <p className="text-lg font-semibold text-gray-900">
              {user?.tenant?.rate_limit_per_minute || 100}/min
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Max Profiles</p>
            <p className="text-lg font-semibold text-gray-900">
              {user?.tenant?.max_profiles || 100}
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

function StatCard({
  label,
  value,
  description,
}: {
  label: string
  value: string | number
  description: string
}) {
  return (
    <div className="card p-6">
      <p className="text-sm text-gray-500">{label}</p>
      <p className="text-3xl font-bold text-gray-900 mt-1">{value}</p>
      <p className="text-xs text-gray-500 mt-2">{description}</p>
    </div>
  )
}
